import {
  type AnyRemoteEnvelope,
  type RemoteConnectionStatus,
  type RemoteEnvelope,
  type RemoteEventType,
  type RemotePayloadMap,
  type RemotePresenceState,
} from '../types/presentationRemote';
import { randomId } from '../utils/presentationRemoteIdentity';
import { createOnlineTransport, type OnlineTransport } from './realtime';

/**
 * 리모컨 전송 계층
 * - local: 같은 브라우저(같은 origin·프로필) 탭끼리 BroadcastChannel — 서버 메시지 0
 * - remote: 온라인 전송(기본 Supabase private 채널 `cp-remote:${userId}`,
 *   REACT_APP_REALTIME_PROVIDER=cloudflare 이면 Cloudflare Durable Object 방 `remote:${userId}`) — services/realtime
 * 대상이 로컬에 있으면 로컬로만 보내고, 받은 쪽은 들어온 경로로 응답한다. commandId 중복 제거는 두 경로 공통.
 */

/** commandId 중복 제거용 LRU */
export class CommandIdLru {
  private map = new Map<string, number>();
  constructor(private readonly cap = 300) {}
  /** 처음 보면 true(처리), 이미 본 id면 false */
  admit(id: string): boolean {
    if (!id) return false;
    if (this.map.has(id)) return false;
    this.map.set(id, Date.now());
    if (this.map.size > this.cap) {
      const first = this.map.keys().next().value;
      if (first !== undefined) this.map.delete(first);
    }
    return true;
  }
}

export type RemoteTransport = 'local' | 'remote';

export interface RemoteChannelOptions {
  userId: string;
  sessionId: string;
  /** 온라인(다른 기기) 연결 여부 (기본 true). 나중에 setRemoteEnabled로 변경 */
  remote?: boolean;
  /** true면 온라인 연결을 절대 열지 않음(로컬 전용) — setRemoteEnabled도 무시 */
  localOnly?: boolean;
  onMessage?: (msg: AnyRemoteEnvelope, via: RemoteTransport) => void;
  onPresence?: (list: RemotePresenceState[]) => void;
  onStatus?: (status: RemoteConnectionStatus) => void;
}

export interface RemoteChannelHandle {
  track: (state: RemotePresenceState) => void;
  send: <T extends RemoteEventType>(
    type: T,
    payload: RemotePayloadMap[T],
    opts?: { targetSessionId?: string; commandId?: string }
  ) => Promise<string | null>;
  /** 명령 전송 후 COMMAND_ACK 대기 (timeout 시 null) */
  sendWithAck: <T extends RemoteEventType>(
    type: T,
    payload: RemotePayloadMap[T],
    opts: { targetSessionId: string; timeoutMs?: number }
  ) => Promise<RemotePayloadMap['COMMAND_ACK'] | null>;
  ack: (to: AnyRemoteEnvelope, ok: boolean, error?: RemotePayloadMap['COMMAND_ACK']['error']) => void;
  getPresence: () => RemotePresenceState[];
  setRemoteEnabled: (on: boolean) => void;
  isRemoteEnabled: () => boolean;
  /** 온라인 연결 수동 재시도 ('다시 연결' 버튼) */
  reconnect: () => void;
  close: () => void;
}

/** 로컬(BroadcastChannel) heartbeat — 네트워크 비용 없음 */
const LOCAL_HEARTBEAT_MS = 5000;
const LOCAL_STALE_MS = 16000;

export const localChannelName = (userId: string) => `cp-pres-remote-local-v1:${userId}`;

type LocalWire =
  | { kind: 'presence'; accountId: string; state: RemotePresenceState }
  | { kind: 'bye'; accountId: string; sessionId: string }
  | { kind: 'query'; accountId: string }
  | { kind: 'msg'; accountId: string; env: AnyRemoteEnvelope };

function isEnvelope(v: unknown): v is AnyRemoteEnvelope {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return typeof o.type === 'string' && typeof o.commandId === 'string' && typeof o.fromSessionId === 'string';
}

/** 실패해도 throw 하지 않는다 — 호출 측 기능에 영향 없도록 조용히 재시도 */
export function openRemoteChannel(opts: RemoteChannelOptions): RemoteChannelHandle {
  const { userId, sessionId } = opts;
  const lru = new CommandIdLru();
  const pendingAcks = new Map<string, (ack: RemotePayloadMap['COMMAND_ACK']) => void>();
  let closed = false;
  let remoteEnabled = !opts.localOnly && opts.remote !== false;
  let trackedState: RemotePresenceState | null = null;
  let remotePresence: RemotePresenceState[] = [];
  let merged: RemotePresenceState[] = [];
  /** 로컬 presence: sessionId → { state, at(수신시각) } */
  const localPeers = new Map<string, { state: RemotePresenceState; at: number }>();
  /** 상대가 마지막으로 어느 경로로 왔는지 (응답 경로) */
  const viaBySession = new Map<string, RemoteTransport>();

  const setStatus = (s: RemoteConnectionStatus) => {
    try {
      opts.onStatus?.(s);
    } catch {
      /* ignore */
    }
  };

  /* ---------- presence merge ---------- */
  const emitPresence = () => {
    const now = Date.now();
    const bySession = new Map<string, RemotePresenceState>();
    remotePresence.forEach((p) => bySession.set(p.sessionId, { ...p, isLocal: false }));
    localPeers.forEach(({ state, at }, sid) => {
      if (now - at > LOCAL_STALE_MS) {
        localPeers.delete(sid);
        return;
      }
      /* 로컬 우선 (같은 sessionId면 로컬 정보로 덮어씀) */
      bySession.set(sid, { ...state, lastSeen: at, isLocal: true });
    });
    merged = Array.from(bySession.values()).filter((p) => p.sessionId !== sessionId);
    try {
      opts.onPresence?.(merged);
    } catch {
      /* ignore */
    }
  };

  /* ---------- incoming ---------- */
  const handleEnvelope = (msg: unknown, via: RemoteTransport) => {
    if (!isEnvelope(msg)) return;
    if (msg.fromSessionId === sessionId) return;
    if (msg.targetSessionId && msg.targetSessionId !== sessionId) return;
    if (!lru.admit(msg.commandId)) return;
    viaBySession.set(msg.fromSessionId, via);
    if (msg.type === 'COMMAND_ACK') {
      const ack = msg.payload as RemotePayloadMap['COMMAND_ACK'];
      const resolve = pendingAcks.get(ack?.commandId);
      if (resolve) {
        pendingAcks.delete(ack.commandId);
        resolve(ack);
      }
    }
    try {
      opts.onMessage?.(msg, via);
    } catch {
      /* ignore */
    }
  };

  /* ---------- local (BroadcastChannel) ---------- */
  let bc: BroadcastChannel | null = null;
  const postLocal = (wire: LocalWire): boolean => {
    if (!bc) return false;
    try {
      bc.postMessage(wire);
      return true;
    } catch {
      return false;
    }
  };
  const announceLocal = () => {
    if (trackedState) postLocal({ kind: 'presence', accountId: userId, state: trackedState });
  };
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      bc = new BroadcastChannel(localChannelName(userId));
      bc.onmessage = (ev: MessageEvent) => {
        const w = ev.data as LocalWire | null;
        /* 계정 격리: 채널명에 userId가 있지만 accountId도 확인 */
        if (!w || typeof w !== 'object' || w.accountId !== userId || closed) return;
        if (w.kind === 'presence') {
          if (!w.state || typeof w.state.sessionId !== 'string' || w.state.sessionId === sessionId) return;
          const isNew = !localPeers.has(w.state.sessionId);
          localPeers.set(w.state.sessionId, { state: w.state, at: Date.now() });
          viaBySession.set(w.state.sessionId, 'local');
          emitPresence();
          if (isNew) announceLocal(); /* 새 피어에게 나를 알림 */
        } else if (w.kind === 'bye') {
          if (localPeers.delete(w.sessionId)) emitPresence();
          viaBySession.delete(w.sessionId);
        } else if (w.kind === 'query') {
          announceLocal();
        } else if (w.kind === 'msg') {
          handleEnvelope(w.env, 'local');
        }
      };
      postLocal({ kind: 'query', accountId: userId });
    } catch {
      bc = null;
    }
  }
  const localTimer = window.setInterval(() => {
    announceLocal();
    emitPresence(); /* stale 정리 */
  }, LOCAL_HEARTBEAT_MS);

  const isLocalTarget = (sid: string | undefined) => {
    if (!sid || !bc) return false;
    const p = localPeers.get(sid);
    if (p && Date.now() - p.at <= LOCAL_STALE_MS) return true;
    return viaBySession.get(sid) === 'local';
  };

  /* ---------- remote (온라인 전송: Supabase 기본 / Cloudflare 선택) ---------- */
  const online: OnlineTransport = createOnlineTransport(
    { userId, sessionId },
    {
      onEnvelope: (raw) => handleEnvelope(raw, 'remote'),
      onPresence: (list) => {
        if (!list.length && !remotePresence.length) return;
        remotePresence = list;
        emitPresence();
      },
      onStatus: (s) => {
        if (!closed) setStatus(s);
      },
    }
  );

  if (remoteEnabled) online.start();
  else setStatus('local');

  /* ---------- send ---------- */
  const send: RemoteChannelHandle['send'] = async (type, payload, sendOpts) => {
    const env: RemoteEnvelope<typeof type> = {
      type,
      commandId: sendOpts?.commandId ?? randomId(),
      fromSessionId: sessionId,
      targetSessionId: sendOpts?.targetSessionId,
      sentAt: Date.now(),
      payload,
    };
    const target = sendOpts?.targetSessionId;
    /* 대상이 같은 브라우저에 있으면 로컬로만 (서버 메시지 사용 안 함) */
    if (target && isLocalTarget(target)) {
      return postLocal({ kind: 'msg', accountId: userId, env: env as AnyRemoteEnvelope }) ? env.commandId : null;
    }
    let sent = false;
    if (!target) sent = postLocal({ kind: 'msg', accountId: userId, env: env as AnyRemoteEnvelope });
    if (online.isReady()) {
      if (await online.send(env as AnyRemoteEnvelope)) sent = true;
    }
    return sent ? env.commandId : null;
  };

  return {
    track: (state) => {
      trackedState = state;
      announceLocal();
      online.track(state);
    },
    send,
    sendWithAck: (type, payload, o) =>
      new Promise((resolve) => {
        const commandId = randomId();
        const timer = window.setTimeout(() => {
          pendingAcks.delete(commandId);
          resolve(null);
        }, o.timeoutMs ?? 5000);
        pendingAcks.set(commandId, (ack) => {
          window.clearTimeout(timer);
          resolve(ack);
        });
        void send(type, payload, { targetSessionId: o.targetSessionId, commandId }).then((id) => {
          if (id) return;
          window.clearTimeout(timer);
          pendingAcks.delete(commandId);
          resolve(null);
        });
      }),
    ack: (to, ok, error) => {
      void send('COMMAND_ACK', { commandId: to.commandId, ok, error }, { targetSessionId: to.fromSessionId });
    },
    getPresence: () => merged,
    setRemoteEnabled: (on) => {
      if (opts.localOnly) on = false;
      if (closed || on === remoteEnabled) return;
      remoteEnabled = on;
      if (on) {
        online.start();
      } else {
        online.stop();
        setStatus('local');
      }
    },
    isRemoteEnabled: () => remoteEnabled,
    reconnect: () => {
      if (closed || !remoteEnabled) return;
      online.reconnect();
    },
    close: () => {
      if (closed) return;
      if (trackedState) postLocal({ kind: 'bye', accountId: userId, sessionId });
      closed = true;
      window.clearInterval(localTimer);
      try {
        bc?.close();
      } catch {
        /* ignore */
      }
      bc = null;
      pendingAcks.clear();
      online.dispose(); /* untrack → removeChannel 순서 */
      setStatus('closed');
    },
  };
}
